import { formatDistanceToNow } from 'date-fns'
import { useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Conversation } from '@/types/chat'
import { Link } from '@tanstack/react-router'
import { Globe, MessageSquare, Phone, Trash2 } from 'lucide-react'

const channelIcons = {
  web: Globe,
  phone: Phone,
  sms: MessageSquare,
} as const

interface ConversationListItemProps {
  conversation: Conversation
  isActive: boolean
  onDelete: (id: string) => void
}

export default function ConversationListItem({
  conversation,
  isActive,
  onDelete,
}: ConversationListItemProps) {
  const Icon = channelIcons[conversation.channel] || Globe
  const [open, setOpen] = useState(false)

  return (
    <div
      className={cn(
        'group flex flex-row w-full items-center hover:bg-accent rounded-lg',
        isActive && 'bg-accent',
      )}
    >
      <Link
        to="/app/chat/$conversationId"
        params={{ conversationId: conversation.id }}
        className="group flex flex-col gap-1 px-3 py-2 text-sm transition-colors overflow-hidden"
      >
        <div className="flex flex-row min-w-0 items-center gap-2">
          <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="truncate font-medium flex-1">
            {conversation.name || 'New conversation'}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(conversation.lastMessageAt || conversation.createdAt), {
              addSuffix: true,
            })}
          </span>
        </div>
        {conversation.tags.length > 0 && (
          <div className="flex gap-1 overflow-hidden">
            {conversation.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </Link>

      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen(true)
        }}
      >
        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogTitle>Delete conversation</AlertDialogTitle>
          <AlertDialogDescription>
            This conversation and all its messages will be permanently deleted.
          </AlertDialogDescription>
          <div className="flex justify-end gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete(conversation.id)}>Delete</AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
